import React, { useState } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert, Modal } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { useWebAlert, AlertButton } from '@/components/WebAlert';
import SignaturePad from '@/components/checklist/SignaturePad';
import { uploadDevChecklistImage, deleteDevChecklistImage } from '@/services/dev/devChecklistImageService';
import { DevChecklistEditorLogger } from '@/utils/logger';

interface DevChecklistSignatureFieldProps {
  empresaId: string;
  checklistId: string;
  fieldId: string;
  value?: string;
  onChange: (uri: string) => void;
  readOnly?: boolean;
}

const logger = DevChecklistEditorLogger.Image;

const DevChecklistSignatureField: React.FC<DevChecklistSignatureFieldProps> = ({
  empresaId,
  checklistId,
  fieldId,
  value,
  onChange,
  readOnly = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { showAlert } = useWebAlert();

  const showMessage = (title: string, msg: string, buttons?: AlertButton[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
    }
  };

  const handleSelectedImage = async (dataUrl: string) => {
    if (readOnly) {
      showMessage('Somente leitura', 'Alterações de assinatura estão temporariamente desabilitadas.');
      return;
    }

    setSaving(true);
    try {
      const result = await uploadDevChecklistImage({
        empresaId,
        checklistId,
        campoId: fieldId,
        localUri: dataUrl,
        kind: 'assinatura',
      });
      onChange(result.publicUrl);
      showMessage('Sucesso', 'Assinatura salva com sucesso!');
    } catch (error: any) {
      logger.warn('[DevChecklistSignatureField] Falha ao salvar assinatura', error);
      showMessage('Erro', error?.message || 'Falha ao salvar a assinatura.');
    } finally {
      setSaving(false);
      setModalOpen(false);
    }
  };

  const handleRemoveSignature = () => {
    if (readOnly) {
      showMessage('Somente leitura', 'Remoções de assinatura estão temporariamente desabilitadas.');
      return;
    }

    const confirmRemove = () => {
      showMessage(
        'Confirmar remoção',
        'Tem certeza que deseja remover esta assinatura?',
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Remover', style: 'destructive', onPress: performRemove },
        ]
      );
    };

    const performRemove = async () => {
      setDeleting(true);
      try {
        await deleteDevChecklistImage(value);
        onChange('');
        showMessage('Sucesso', 'Assinatura removida com sucesso!');
      } catch (error: any) {
        logger.warn('[DevChecklistSignatureField] Falha ao remover assinatura', error);
        showMessage('Erro', error?.message || 'Falha ao remover a assinatura.');
      } finally {
        setDeleting(false);
      }
    };

    confirmRemove();
  };

  return (
    <View style={styles.container}>
      {value ? (
        <>
          <Image source={{ uri: value }} style={styles.preview} />
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.button}
              onPress={() => setModalOpen(true)}
              disabled={readOnly || saving || deleting}
            >
              {saving ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Text style={styles.buttonText}>Alterar assinatura</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.buttonDanger]}
              onPress={handleRemoveSignature}
              disabled={readOnly || saving || deleting}
            >
              {deleting ? (
                <ActivityIndicator size="small" color={theme.colors.util.error} />
              ) : (
                <Text style={[styles.buttonText, styles.buttonDangerText]}>Remover</Text>
              )}
            </TouchableOpacity>
          </View>
        </>
      ) : (
        <TouchableOpacity
          style={styles.emptyButton}
          onPress={() => setModalOpen(true)}
          disabled={readOnly || saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color={theme.colors.primary.default} />
          ) : (
            <>
              <MaterialCommunityIcons name="draw-pen" size={18} color={theme.colors.primary.default} />
              <Text style={styles.emptyButtonText}>Adicionar assinatura</Text>
            </>
          )}
        </TouchableOpacity>
      )}

      <Modal visible={modalOpen} transparent animationType="slide">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContent}>
            <SignaturePad
              onOK={handleSelectedImage}
              onClose={() => setModalOpen(false)}
            />
            <TouchableOpacity style={styles.modalClose} onPress={() => setModalOpen(false)}>
              <Ionicons name="close" size={20} color={theme.colors.text.primary} />
              <Text style={styles.modalCloseText}>Fechar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing.sm,
  },
  preview: {
    width: '100%',
    height: 200,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
  },
  actions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  button: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: '500' as any,
  },
  buttonDanger: {
    borderColor: theme.colors.util.error,
  },
  buttonDangerText: {
    color: theme.colors.util.error,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
  },
  emptyButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: '500' as any,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.md,
  },
  modalContent: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  modalClose: {
    marginTop: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
  },
  modalCloseText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
});

export default DevChecklistSignatureField;
